import React, { useEffect, useMemo, useState } from 'react';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import MemberStatsCard from './components/MemberStatsCard';
import MemberFilters from './components/MemberFilters';
import BulkActionsToolbar from './components/BulkActionsToolbar';
import MemberTable from './components/MemberTable';
import MemberEditModal from './components/MemberEditModal';
import MemberHistoryModal from './components/MemberHistoryModal';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabaseClient';


const MemberManagement = () => {
  const [filters, setFilters] = useState({
    search: '',
    membershipType: 'all',
    skillLevel: 'all',
    dateFrom: '',
    dateTo: ''
  });

  const [selectedMembers, setSelectedMembers] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'registeredAt', direction: 'desc' });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);

  const getSkillDescription = (skillLevel) => {
    const level = parseFloat(skillLevel);
    if (Number.isNaN(level)) return '';
    if (level <= 1.0) return '입문';
    if (level <= 1.5) return '초급';
    if (level <= 2.0) return '초급+';
    if (level <= 2.5) return '초중급';
    if (level <= 3.0) return '중급';
    if (level <= 3.5) return '중급+';
    if (level <= 4.0) return '고급';
    if (level <= 4.5) return '고급+';
    return '전문가';
  };

  const mapMemberRow = (row) => {
    if (!row) return null;
    const skillDescription = getSkillDescription(row.skill_level);
    return {
      id: row.id,
      name: row.name,
      phone: row.phone,
      skillLevel: row.skill_level,
      skillDescription,
      membershipType: row.membership_type,
      membershipExpiry: row.membership_expiry,
      status: row.status,
      registeredAt: row.registered_at,
      notes: row.notes
    };
  };

  const loadMembers = async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session?.user?.id) {
        setMembers([]);
        setLoadError('로그인이 필요합니다.');
        return;
      }

      const { data, error } = await supabase
        .from('members')
        .select('*')
        .order('registered_at', { ascending: false });

      if (error) {
        setLoadError(error?.message || '회원 목록을 불러오지 못했습니다.');
        setMembers([]);
        return;
      }

      setMembers((data || []).map(mapMemberRow).filter(Boolean));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, []);

  const filteredMembers = useMemo(() => {
    let filtered = members?.filter(member => {
      const matchesSearch = 
        member?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        member?.phone?.includes(filters?.search);
      
      const matchesMembership = filters?.membershipType === 'all' || member?.membershipType === filters?.membershipType;
      const matchesStatus = true;
      
      let matchesSkillLevel = true;
      if (filters?.skillLevel !== 'all') {
        const level = parseFloat(member?.skillLevel);
        switch (filters?.skillLevel) {
          case 'beginner':
            matchesSkillLevel = level >= 1.0 && level <= 2.5;
            break;
          case 'intermediate':
            matchesSkillLevel = level >= 3.0 && level <= 3.5;
            break;
          case 'advanced':
            matchesSkillLevel = level >= 4.0 && level <= 4.5;
            break;
          case 'expert':
            matchesSkillLevel = level >= 5.0;
            break;
        }
      }

      let matchesDateRange = true;
      if (filters?.dateFrom) {
        matchesDateRange = new Date(member.registeredAt) >= new Date(filters.dateFrom);
      }
      if (filters?.dateTo && matchesDateRange) {
        matchesDateRange = new Date(member.registeredAt) <= new Date(filters.dateTo);
      }

      return matchesSearch && matchesMembership && matchesStatus && matchesSkillLevel && matchesDateRange;
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];

        if (sortConfig?.key === 'skillLevel') {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        }

        if (aValue < bValue) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [members, filters, sortConfig]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((filteredMembers?.length || 0) / PAGE_SIZE));
  }, [filteredMembers, PAGE_SIZE]);

  useEffect(() => {
    setPage(1);
  }, [filters, sortConfig]);

  useEffect(() => {
    setPage(p => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const paginatedMembers = useMemo(() => {
    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE;
    return (filteredMembers || []).slice(from, to);
  }, [filteredMembers, page, PAGE_SIZE]);

  const activeMembers = members?.filter(m => m?.status === 'active');
  const suspendedMembers = members?.filter(m => m?.status === 'suspended');

  const stats = {
    total: members?.length,
    active: activeMembers?.length,
    suspended: suspendedMembers?.length
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      membershipType: 'all',
      skillLevel: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleSelectMember = (memberId, checked) => {
    if (checked) {
      setSelectedMembers(prev => [...prev, memberId]);
    } else {
      setSelectedMembers(prev => prev?.filter(id => id !== memberId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedMembers(prev => {
        const next = new Set(prev || []);
        (paginatedMembers || []).forEach(m => {
          if (m?.id) next.add(m.id);
        });
        return Array.from(next);
      });
    } else {
      setSelectedMembers(prev => {
        const removeIds = new Set((paginatedMembers || []).map(m => m?.id).filter(Boolean));
        return (prev || []).filter(id => !removeIds.has(id));
      });
    }
  };

  const handleClearSelection = () => {
    setSelectedMembers([]);
  };

  const handleBulkSuspend = async () => {
    if (!selectedMembers?.length) return;
    const { error } = await supabase
      .from('members')
      .update({ status: 'suspended' })
      .in('id', selectedMembers);
    if (error) {
      alert(error?.message || '정지 처리에 실패했습니다');
      return;
    }
    await loadMembers();
    setSelectedMembers([]);
  };

  const handleBulkActivate = async () => {
    if (!selectedMembers?.length) return;
    const { error } = await supabase
      .from('members')
      .update({ status: 'active' })
      .in('id', selectedMembers);
    if (error) {
      alert(error?.message || '활성화 처리에 실패했습니다');
      return;
    }
    await loadMembers();
    setSelectedMembers([]);
  };

  const handleBulkDelete = async () => {
    if (!selectedMembers?.length) return;
    const ok = window.confirm(`선택한 ${selectedMembers.length}명의 회원을 삭제할까요?`);
    if (!ok) return;

    const { error } = await supabase
      .from('members')
      .delete()
      .in('id', selectedMembers);
    if (error) {
      alert(error?.message || '삭제에 실패했습니다');
      return;
    }
    await loadMembers();
    setSelectedMembers([]);
  };

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleEdit = (member) => {
    setSelectedMember(member);
    setEditModalOpen(true);
  };

  const handleAddMember = () => {
    setSelectedMember(null);
    setEditModalOpen(true);
  };

  const handleViewHistory = (member) => {
    setSelectedMember(member);
    setHistoryModalOpen(true);
  };

  const handleRenew = (member) => {
    alert(`${member?.name}님의 회원권을 갱신합니다`);
  };

  const handleSaveMember = async (updatedMember) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) throw new Error('로그인이 필요합니다.');

    if (updatedMember?.id) {
      const { error } = await supabase
        .from('members')
        .update({
          name: updatedMember?.name,
          phone: updatedMember?.phone,
          skill_level: updatedMember?.skillLevel,
          membership_type: updatedMember?.membershipType,
          membership_expiry: updatedMember?.membershipExpiry || null,
          notes: updatedMember?.notes,
          status: updatedMember?.status
        })
        .eq('id', updatedMember.id);

      if (error) throw new Error(error?.message || '저장에 실패했습니다.');
      await loadMembers();
      alert(`${updatedMember?.name}님의 정보가 저장되었습니다`);
      return;
    }

    const { error } = await supabase
      .from('members')
      .insert({
        client_id: userId,
        name: updatedMember?.name,
        phone: updatedMember?.phone,
        skill_level: updatedMember?.skillLevel,
        membership_type: updatedMember?.membershipType || 'none',
        membership_expiry: updatedMember?.membershipExpiry || null,
        notes: updatedMember?.notes,
        status: updatedMember?.status
      });

    if (error) throw new Error(error?.message || '저장에 실패했습니다.');
    await loadMembers();
    alert(`${updatedMember?.name}님이 추가되었습니다`);
  };

  const handleViewDetails = (member) => {
    setSelectedMember(member);
    setHistoryModalOpen(true);
  };

  return (
    <OpenPlayToolsLayout userRole="player">
      <main className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">맴버</h1>
            <p className="text-base md:text-lg caption text-muted-foreground">회원 정보를 관리하세요</p>
          </div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                회원 관리
              </h1>
              <p className="text-base md:text-lg caption text-muted-foreground">
                당신만의 회원을 등록해 데이터베이스를 구축하세요
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              iconName="UserPlus"
              iconPosition="left"
              onClick={handleAddMember}
            >
              회원 추가
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 md:gap-6 mb-6 md:mb-8">
            <MemberStatsCard
              icon="Users"
              label="전체 회원"
              value={stats?.total}
              trend="up"
              trendValue="+12%"
              color="primary"
            />
            <MemberStatsCard
              icon="CheckCircle"
              label="활성 회원"
              value={stats?.active}
              color="success"
            />
            <MemberStatsCard
              icon="Ban"
              label="정지된 회원"
              value={stats?.suspended}
              color="accent"
            />
          </div>

          {loadError && (
            <div className="mb-6 md:mb-8 bg-error/10 border border-error/20 rounded-xl p-4">
              <p className="text-sm text-error">{loadError}</p>
            </div>
          )}

          <div className="space-y-6">
            <MemberFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
              resultCount={filteredMembers?.length}
            />

            <BulkActionsToolbar
              selectedCount={selectedMembers?.length}
              onSuspend={handleBulkSuspend}
              onActivate={handleBulkActivate}
              onDelete={handleBulkDelete}
              onClearSelection={handleClearSelection}
            />

            <div className="bg-card border border-border rounded-xl p-4 md:p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-heading font-semibold text-foreground">
                  회원 목록
                </h2>
              </div>

              <MemberTable
                members={paginatedMembers}
                selectedMembers={selectedMembers}
                onSelectMember={handleSelectMember}
                onSelectAll={handleSelectAll}
                onEdit={handleEdit}
                onViewHistory={handleViewHistory}
                onRenew={handleRenew}
                sortConfig={sortConfig}
                onSort={handleSort}
                isLoading={isLoading}
              />

              <div className="mt-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="text-sm caption text-muted-foreground">
                  총 {filteredMembers?.length || 0}명 / {page} / {totalPages} 페이지
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    이전
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    다음
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <MemberEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        member={selectedMember}
        onSave={handleSaveMember}
      />
      <MemberHistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        member={selectedMember}
      />
    </OpenPlayToolsLayout>
  );
};

export default MemberManagement;